import React, { useEffect, useState } from "react";
import Papa from "papaparse";
import {
  Table,
  Input,
  Tag,
  Form,
  Button,
  Space,
  Modal,
  Select,
  Popconfirm,
  message,
  Tooltip,
  Descriptions,
  Radio,
} from "antd";
import { Helmet } from "react-helmet";
import { Typography } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import RequirePermission from "../components/Common/RequirePermission";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";

const { Title } = Typography;

const Devices = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const [devices, setDevices] = useState([]);
  const [totalDevices, setTotalDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterText, setFilterText] = useState("");
  const [viewPermission, setViewPermission] = useState(false);
  const [editPermission, setEditPermission] = useState(false);
  const [deletePermission, setDeletePermission] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [user, setUser] = useState(null);

  const setQueryParams = (params) => {
    const searchParams = new URLSearchParams(params);
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setFilterText(value);
    const newQueryParams = {
      city: selectedCity,
      location: selectedLocation,
      filter: value,
      limit: pageSize,
      page: 1,
    };

    setQueryParams(newQueryParams);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleExport = async () => {
    const response = await axios.get(
      process.env.REACT_APP_BASE_URL + `/api/meeting_room/lists`,
    );
    const devices = response.data.data.data;
    const exportData = devices.map(
      ({ updated_at, created_at, meeting_room_id, ...rest }) => ({
        ...rest,
        total_memory:
          rest.total_memory > 1024 * 1024 * 1024
            ? (rest.total_memory / (1024 * 1024 * 1024)).toFixed(2) + " GB"
            : (rest.total_memory / (1024 * 1024)).toFixed(2) + " MB",
        free_disk:
          rest.free_disk > 1024 * 1024 * 1024
            ? (rest.free_disk / (1024 * 1024 * 1024)).toFixed(2) + " GB"
            : (rest.free_disk / (1024 * 1024)).toFixed(2) + " MB",
        is_connected: rest.is_connected ? "Yes" : "No",
        is_tablet: rest.is_tablet ? "Yes" : "No",
        meeting_room:
          rest.meeting_room !== null ? rest.meeting_room : "Unassigned",
        building: rest.building !== null ? rest.building : "-",
        city: rest.city !== null ? rest.city : "-",
      }),
    );

    const csv = Papa.unparse(exportData);

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    const timeStamps = dayjs().format("YYYY-MM-DD_HH-mm-ss");
    const fileName = `devices_data_${timeStamps}.csv`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  };

  const handleEdit = (record) => {
    setIsEditModalOpen(true);
    setSelectedStatus(record.status);
    setSelectedId(record.id);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/meeting_room/${id}`,
      );
      const updated_row_count = response.data.data.deleted_row_count;
      if (updated_row_count === 1) {
        message.success("Device deleted successfully");
        await fetchData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      setIsEditModalOpen(false);
      const response = await axios.put(
        process.env.REACT_APP_BASE_URL +
          `/api/meeting_room/status/${selectedId}`,
        {
          status: selectedStatus,
        },
      );
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count >= 1) {
        message.success("Status updated successfully");
        await fetchData();
      }
    } catch (error) {
      message.error("Error updating status");
      console.error("Error updating status", error);
    }
  };

  const handleLocationChange = (value) => {
    setSelectedLocation(value);
    const newQueryParams = {
      city: selectedCity,
      location: value,
      filter: filterText,
      limit: pageSize,
      page: 1,
    };

    setQueryParams(newQueryParams);
    setCurrentPage(1);
  };

  const handleCityChange = (value) => {
    setSelectedCity(value);
    const newQueryParams = {
      city: value,
      location: selectedLocation,
      filter: filterText,
      limit: pageSize,
      page: 1,
    };

    setQueryParams(newQueryParams);
    setCurrentPage(1);
  };

  const handleView = async (record) => {
    try {
      setModalLoading(true);
      setIsViewModalOpen(true);
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `/api/meeting_room/${record.id}`,
      );
      setDetail(response.data.data);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching screen data", error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleOpenMap = async (lat, lng) => {
    try {
      const baseUrl = "https://www.google.com/maps/search/?api=1";
      const query = encodeURIComponent(`${lat},${lng}`);
      const url = `${baseUrl}&query=${query}`;
      window.open(url, "_blank");
    } catch (error) {
      message.error("Error opening map");
      console.error("Error fetching screen data", error);
    }
  };

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/meeting_room/masters`,
      );
      setStatusOptions(response.data.data.status);
      setLocations(response.data.data.locationList);
      setLocationOptions(response.data.data.locationList);
      setCityOptions([
        { id: 0, name: "Unassigned" },
        ...response.data.data.cityList,
      ]);
    } catch (error) {
      console.error("Failed to fetch master data", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `/api/meeting_room`,
        {
          params: {
            city: selectedCity,
            location: selectedLocation,
            filter: filterText,
            limit: pageSize,
            page: currentPage,
          },
        },
      );
      let records = response.data.data.data;
      setTotalRecords(response.data.data.total);
      setDevices(records);
    } catch (error) {
      console.error("Error fetching locations data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("userData"));
    setUser(storedUser);
    const permissions = storedUser.permissions;
    setViewPermission(parseInt(permissions["signage.meeting_room_view"]) === 1);
    setEditPermission(parseInt(permissions["signage.meeting_room_edit"]) === 1);
    setDeletePermission(
      parseInt(permissions["signage.meeting_room_delete"]) === 1,
    );
  }, []);

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    const newQueryParams = {
      city: selectedCity,
      location: selectedLocation,
      filter: filterText,
      limit: queryParams.get("limit") ?? 10,
      page: queryParams.get("page") ?? 1,
    };

    if (!selectedCity) {
      setLocationOptions(locations);
    }
    if (selectedCity) {
      setLocationOptions(
        locations.filter((location) => location.city_id === selectedCity),
      );
    }

    if (
      JSON.stringify(newQueryParams) !==
      JSON.stringify(Object.fromEntries(new URLSearchParams(location.search)))
    ) {
      setQueryParams(newQueryParams);
    }

    setQueryParams(newQueryParams);
    const fetchTimer = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(fetchTimer);
  }, [currentPage, pageSize, filterText, selectedCity, selectedLocation]);

  const columns = [
    {
      title: "Device ID",
      dataIndex: "device_id",
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: "Device Name",
      dataIndex: "device_name",
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: "Meeting Room",
      dataIndex: "meeting_room",
      render: (text) =>
        text ? (
          <Tag>{text.toUpperCase()}</Tag>
        ) : (
          <Tag>
            <span>Unassigned</span>
          </Tag>
        ),
    },
    {
      title: "Building",
      dataIndex: "building",
      render: (text) => (
        <span className="text-small">{text ? text.toUpperCase() : "-"}</span>
      ),
    },
    {
      title: "City",
      dataIndex: "city",
      render: (text) => (
        <span className="text-small">{text ? text.toUpperCase() : "-"}</span>
      ),
    },
    {
      title: "Model",
      dataIndex: "model",
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: "Platform",
      dataIndex: "platform_name",
      render: (text, record) => (
        <Tag>{record.system_name + " " + record.system_version}</Tag>
      ),
    },
    {
      title: "Version Number",
      dataIndex: "app_version",
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: "Public Ip",
      dataIndex: "public_ip",
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: "status",
      dataIndex: "status",
      render: (text) => (
        <Tag
          color={
            text === "active" ? "green" : text === "inactive" ? "blue" : "red"
          }
        >
          {text.slice(0, 1).toUpperCase() + text.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Connection Status",
      dataIndex: "screen_connection_status",
      render: (status, record) =>
        record.is_connected === 1 ? (
          <Tag color="green">Connected</Tag>
        ) : (
          <Tag color="red">Disconnected</Tag>
        ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (text) => (
        <span className="text-small">
          {dayjs(text).format("DD-MM-YYYY HH:mm")}
        </span>
      ),
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      render: (text) => (
        <span className="text-small">
          {text ? dayjs(text).format("DD-MM-YYYY HH:mm") : "Not Updated"}
        </span>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          {user && (
            <>
              {viewPermission && (
                <Tooltip title="View Device">
                  <Button
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                </Tooltip>
              )}
              {editPermission && (
                <Tooltip title="Edit Device">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                </Tooltip>
              )}
              {deletePermission && (
                <Tooltip title="Delete Device">
                  <Popconfirm
                    title="Are you sure to delete this device?"
                    onConfirm={() => handleDelete(record.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button danger size="small" icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Tooltip>
              )}
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <RequirePermission permission="signage.meeting_room_view">
      <div style={{ padding: "1rem" }}>
        <Helmet>
          <title>{`${process.env.REACT_APP_NAME} | Meeting Room Devices`}</title>
        </Helmet>
        <Title level={3} style={{ marginBottom: "1rem" }}>
          Meeting Room Devices
        </Title>
        <Form className="d-flex justify-content-between">
          <div>
            <Form.Item>
              <Input.Search
                placeholder="Search Meeting Room Device"
                value={filterText}
                onChange={handleSearch}
                style={{ width: 200 }}
              />
              <Select
                value={selectedCity}
                onChange={handleCityChange}
                placeholder="Select City"
                style={{ width: 200, marginLeft: 10 }}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  return (option?.children ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
              >
                {cityOptions.map((city) => (
                  <Select.Option key={city.id} value={city.id}>
                    {city.name}
                  </Select.Option>
                ))}
              </Select>
              <Select
                value={selectedLocation}
                onChange={handleLocationChange}
                placeholder="Select Building"
                style={{ width: 200, marginLeft: 10 }}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  return (option?.children ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
              >
                {locationOptions.map((location) => (
                  <Select.Option key={location.id} value={location.id}>
                    {location.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div>
            <Form.Item>
              <Button type="primary" onClick={handleExport}>
                Export Device Data
              </Button>
            </Form.Item>
          </div>
        </Form>

        <Table
          loading={loading}
          columns={columns}
          dataSource={devices}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRecords,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} devices`,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50", "100"],
          }}
        />
        <Modal
          title="View Device"
          open={isViewModalOpen}
          onCancel={() => {
            setIsViewModalOpen(false);
          }}
          loading={modalLoading}
          width={{
            xs: "90%",
            sm: "80%",
            md: "70%",
            lg: "60%",
            xl: "50%",
            xxl: "40%",
          }}
          footer={null}
        >
          {detail && (
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="ID">
                {detail.device_id.toUpperCase()}
              </Descriptions.Item>
              <Descriptions.Item label="Name">
                {detail.device_name.toUpperCase()}
              </Descriptions.Item>
              <Descriptions.Item label="Model">
                {detail.model}
              </Descriptions.Item>
              <Descriptions.Item label="Network Type">
                {detail.network_type}
              </Descriptions.Item>
              <Descriptions.Item label="Is Tablet">
                {detail.is_tablet ? (
                  <Tag color="green">Yes</Tag>
                ) : (
                  <Tag color="red">No</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Meeting Room Name">
                {detail.meeting_room
                  ? detail.meeting_room?.toUpperCase()
                  : "Unassigned"}
              </Descriptions.Item>
              <Descriptions.Item label="Building">
                {detail.location ? detail.location : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="City">
                {detail.city ? detail.city : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Total Memory">
                {detail.total_memory > 1024 * 1024 * 1024 ? (
                  <>
                    {(detail.total_memory / 1024 / 1024 / 1024).toFixed(2)} GB
                  </>
                ) : (
                  <>{(detail.total_memory / 1024 / 1024).toFixed(2)} MB</>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Free Memory">
                {detail.free_disk > 1024 * 1024 * 1024 ? (
                  <>{(detail.free_disk / 1024 / 1024 / 1024).toFixed(2)} GB</>
                ) : (
                  <>{(detail.free_disk / 1024 / 1024).toFixed(2)} MB</>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Local IP">
                {detail.local_ip}
              </Descriptions.Item>
              <Descriptions.Item label="Public IP">
                {detail.public_ip}
              </Descriptions.Item>
              <Descriptions.Item label="App Version">
                {detail.app_version}
              </Descriptions.Item>
              <Descriptions.Item label="Manufacturer">
                {detail.manufacturer.toUpperCase()}
              </Descriptions.Item>
              <Descriptions.Item label="Device Location">
                <>
                  <span>
                    <b>Lat: </b>
                    {detail.latitude}
                  </span>
                  &nbsp; &nbsp;
                  <span>
                    <b>Lng: </b>
                    {detail.longitude}
                  </span>
                  <Button
                    size="small"
                    onClick={() =>
                      handleOpenMap(detail.latitude, detail.longitude)
                    }
                    style={{
                      border: "none",
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      color: "blue",
                      position: "relative",
                      top: "2px",
                      left: "5px",
                    }}
                  >
                    <img
                      src="https://icons.veryicon.com/png/o/photographic/ant-design-official-icon-library/location-37.png"
                      alt="Device Location"
                      style={{ width: "15px", height: "15px" }}
                    />
                  </Button>
                </>
              </Descriptions.Item>
              <Descriptions.Item label="Connection Status">
                <Tag color={detail.is_connected ? "green" : "red"}>
                  {detail.is_connected ? "Connected" : "Disconnected"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {detail.created_at}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {detail.updated_at}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
        {isEditModalOpen && (
          <Modal
            title="Edit Device Status"
            open={isEditModalOpen}
            onCancel={() => {
              setIsEditModalOpen(false);
            }}
            width={400}
            footer={null}
            maskClosable={false}
          >
            <form onSubmit={handleStatusUpdate}>
              <label className="form-label">Status:</label>
              <Radio.Group
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                }}
                value={selectedStatus}
                style={{ marginLeft: "5px", marginTop: "5px" }}
              >
                {statusOptions.map((option) => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Radio.Group>
              <button type="submit" className="btn btn-primary btn-sm mt-3">
                Submit
              </button>
            </form>
          </Modal>
        )}
      </div>
    </RequirePermission>
  );
};

export default Devices;
